"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var d3 = _interopRequireWildcard(require("d3"));
var _useChartDimensions = require("../hooks/useChartDimensions");
var _useChartTooltip = require("../hooks/useChartTooltip");
var _lodash = _interopRequireDefault(require("lodash.findlast"));
var _SvgChart = _interopRequireDefault(require("../components/SvgChart"));
var _labelFormatter = require("../utils/label-formatter");
var _useChartData = require("../hooks/useChartData");
var _BarChart = require("./BarChart.styled");
var _useAxisOverlap = require("../hooks/useAxisOverlap");
var _chartOperations = require("../utils/chart-operations");
var _useFilter = require("../hooks/useFilter");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
const BASE_MARGIN = Object.freeze({
  top: 20,
  bottom: 50,
  left: 0,
  right: 0
});
const Y_AXIS_PADDING = 0.05;
const BAR_GAP = 0.15;
const ANIMATION_DURATION = 700;
const ANIMATION_BAR_DELAY = 100;
const BarChart = /*#__PURE__*/(0, _react.memo)(chartProps => {
  var _chart$colors;
  const {
    chart,
    chartColors,
    animate
  } = chartProps;
  const {
    xCol,
    yCol,
    yFormat,
    componentProps
  } = chart;
  const {
    showBarValues = true,
    showYAxis = false
  } = componentProps;
  const palette = ((_chart$colors = chart.colors) === null || _chart$colors === void 0 ? void 0 : _chart$colors.schemes.categorical) || chartColors.schemes.categorical;
  const {
    filter,
    filterComponent
  } = (0, _useFilter.useFilter)(chartProps);
  const [animationDone, setAnimated] = (0, _react.useState)(!animate);
  const svgRef = _react.default.useRef(null);
  const {
    detectLabelOverlap,
    labelOverlap
  } = (0, _useAxisOverlap.useAxisOverlap)(svgRef, ".x-axis");
  const baseMargin = Object.assign({}, {
    ...BASE_MARGIN
  }, {
    left: showYAxis ? 30 : 0
  });
  let {
    width,
    height,
    margin
  } = (0, _useChartDimensions.useChartDimensions)(svgRef, baseMargin, chart.spanRow, ".x-axis .tick text, .y-axis .tick text");
  const {
    data,
    seriesLabels,
    xLabels,
    xKeys,
    yMax,
    yMin
  } = (0, _useChartData.useChartData)(chart, filter);
  const {
    tooltipRef,
    showTooltip,
    hideTooltip
  } = (0, _useChartTooltip.useChartTooltip)(chart, data);
  const xScale = d3.scaleBand().domain(xKeys).range([0, width]).padding(BAR_GAP);
  const groupedData = xLabels.map(xLabel => {
    return {
      xLabel,
      points: data.map(d => {
        let value = (0, _lodash.default)(d.points, {
          [xCol]: xLabel.key
        });
        let point = 0;
        if (value !== undefined) {
          point = Number(value[yCol]);
          if (point === undefined || isNaN(point)) {
            point = 0;
          }
        }
        return {
          seriesKey: d.seriesKey,
          point
        };
      })
    };
  });
  const xSubgroup = d3.scaleBand().domain(seriesLabels.map(s => s.key)).range([0, xScale.bandwidth()]).padding(0.1);
  const colorPalette = d3.scaleOrdinal().domain(seriesLabels.map(s => s.key)).range(palette);
  const yRange = Math.abs(yMax - yMin);
  const yPadding = yRange * Y_AXIS_PADDING;
  const maxAbsValue = Math.max(Math.abs(yMax), Math.abs(yMin));
  const maxDomain = maxAbsValue + yPadding;
  height *= Math.min(1, maxAbsValue / yRange);
  if (yMin < 0) {
    height -= 10;
  }
  const yScale = d3.scaleLinear().domain([0, maxDomain]).range([height, 0]);
  let top = margin.top;
  let axisPadding = 0;
  if (yMin < 0) {
    if (Math.sign(yMin) === Math.sign(yMax)) {
      top -= height - yScale(maxAbsValue);
    } else {
      top -= yScale(yMax);
    }
    axisPadding = height - yScale(Math.abs(yMin)) + 18;
  }
  (0, _react.useLayoutEffect)(() => {
    if (svgRef.current === null || !height) {
      return;
    }
    const xAxisScale = xScale.domain(xLabels.map(xVal => xVal.label));
    const xAxis = d3.axisBottom(xAxisScale).tickSize(labelOverlap ? 5 : 0).tickPadding((labelOverlap ? 2 : 12) + axisPadding);
    d3.select(svgRef.current).select(".x-axis").attr("transform", "translate(0, ".concat(height, ")")).call(xAxis);
    d3.select(svgRef.current).selectAll(".x-axis .tick line").attr("transform", "translate(0, ".concat(axisPadding, ")"));
    if (showYAxis) {
      let yAxis = d3.axisLeft(yScale).tickSize(0).tickPadding(8).ticks(8).tickFormat(v => _labelFormatter.QuantitativeFormatter[yFormat](v.toString()));
      d3.select(svgRef.current).select(".y-axis").classed("y-axis", true).call(yAxis);
    }
    detectLabelOverlap();
  }, [height, xLabels, axisPadding, labelOverlap, xScale, yScale, showYAxis, yFormat]);
  (0, _react.useLayoutEffect)(() => {
    if (!svgRef.current || !width) {
      return;
    }
    const labels = svgRef.current.querySelectorAll(".bar-chart text.bar-label");
    for (let i = 0; i < labels.length; i++) {
      const label1 = labels[i].getBoundingClientRect();
      for (let j = i + 1; j < Math.min(i + 2, labels.length); j++) {
        const label2 = labels[j].getBoundingClientRect();
        if ((0, _chartOperations.boxesIntersect)(label1, label2)) {
          const moveFirst = label2.y > label1.y;
          const moveIdx = moveFirst ? i : j;
          const y = Number(labels[moveIdx].getAttribute("y") || 0);
          const dy = Number(labels[moveIdx].getAttribute("dy") || 0);
          if (dy < 0 && y > -dy) {
            labels[moveIdx].setAttribute("dy", (dy - 0.5).toString());
            i = Math.max(-1, i - 4);
          }
          break;
        }
      }
    }
    return () => {
      for (let i = 0; i < labels.length; i++) {
        const negative = labels[i].textContent.startsWith("-");
        if (!negative) {
          labels[i].setAttribute("dy", "-6");
        }
      }
    };
  }, [svgRef.current, chart, width]);
  const handleVisible = (0, _react.useCallback)(() => {
    if (animationDone) return;
    const points = groupedData.map(group => group.points).flat();
    const chart = d3.select(svgRef.current).select(".bar-chart");
    chart.selectAll("rect").data(points).transition().duration(ANIMATION_DURATION).attr("y", p => {
      return yScale(Math.max(p.point, 0));
    }).attr("height", p => {
      return height - yScale(Math.abs(p.point));
    }).delay((_, i) => i * ANIMATION_BAR_DELAY);
    svgRef.current.querySelectorAll(".bar-group").forEach(group => group.classList.add("full"));
    window.setTimeout(() => setAnimated(true), ANIMATION_DURATION + points.length * ANIMATION_BAR_DELAY);
  }, [groupedData, animationDone, height, yScale]);
  let timeout;
  function handleMouseEnter(e) {
    var _svgRef$current, _svgRef$current2;
    clearTimeout(timeout);
    const el = e.target;
    showTooltip(el);
    (_svgRef$current = svgRef.current) === null || _svgRef$current === void 0 ? void 0 : _svgRef$current.querySelectorAll("rect").forEach(rect => rect.classList.add("semitransparent"));
    (_svgRef$current2 = svgRef.current) === null || _svgRef$current2 === void 0 ? void 0 : _svgRef$current2.querySelectorAll("rect[data-series=\"".concat(el.dataset.series, "\"]")).forEach(rect => rect.classList.remove("semitransparent"));
  }
  function handleMouseLeave(e) {
    hideTooltip();
    clearTimeout(timeout);
    timeout = window.setTimeout(() => {
      var _svgRef$current3;
      (_svgRef$current3 = svgRef.current) === null || _svgRef$current3 === void 0 ? void 0 : _svgRef$current3.querySelectorAll("rect").forEach(rect => rect.classList.remove("semitransparent"));
    }, 300);
  }
  return /*#__PURE__*/_react.default.createElement(_SvgChart.default, {
    margin: {
      ...margin,
      top
    },
    svgRef: svgRef,
    tooltipRef: tooltipRef,
    chart: chart,
    palette: palette,
    onVisible: handleVisible,
    filterComponent: filterComponent
  }, /*#__PURE__*/_react.default.createElement(_BarChart.BarChartGroup, {
    className: "bar-chart"
  }, groupedData.map((group, gIdx) => /*#__PURE__*/_react.default.createElement("g", {
    key: group.xLabel.key,
    className: "bar-group ".concat(!animationDone ? "" : "full"),
    transform: "translate(".concat(xScale(group.xLabel.key), ", 0)")
  }, group.points.map((p, idx) => /*#__PURE__*/_react.default.createElement("g", {
    key: "".concat(group.xLabel.key, "-").concat(p.seriesKey)
  }, /*#__PURE__*/_react.default.createElement("rect", {
    x: xSubgroup(p.seriesKey),
    y: !animationDone ? yScale(0) : yScale(Math.max(p.point, 0)),
    width: xSubgroup.bandwidth(),
    height: !animationDone ? 0 : height - yScale(Math.abs(p.point)),
    fill: colorPalette(p.seriesKey),
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    "data-x": group.xLabel.key,
    "data-series": p.seriesKey,
    "data-color": colorPalette(p.seriesKey)
  }), showBarValues && /*#__PURE__*/_react.default.createElement("text", {
    className: "bar-label",
    textAnchor: "middle",
    dx: xSubgroup.bandwidth() / 2,
    dy: p.point >= 0 ? -6 : 14,
    x: xSubgroup(p.seriesKey),
    y: yScale(p.point),
    style: {
      /** @ts-ignore */
      "--animation-delay": "".concat((gIdx * group.points.length + idx + 5) / 10, "s")
    }
  }, _labelFormatter.QuantitativeFormatter[yFormat](p.point))))))), /*#__PURE__*/_react.default.createElement("g", {
    className: "x-axis"
  }), /*#__PURE__*/_react.default.createElement("g", {
    className: "y-axis"
  }));
});
var _default = BarChart;
exports.default = _default;